import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import Head from 'next/head'
import { OnboardingHeader } from 'routes/components/OnboardingHeader/OnboardingHeader'
import { OnboardingLayout } from 'routes/components/OnboardingLayout/OnboardingLayout'
import { OnboardingLayoutProvider } from 'routes/components/OnboardingLayout/hooks/useOnboardingLayoutContext/useOnboardingLayoutContext'
import { MetaTags } from 'routes/components/MetaTags/MetaTags'
import { useLogEventOnMount } from 'lib/hooks/useLogEventOnMount/useLogEventOnMount'
import { useTrackOnboardingPageOnMount } from 'lib/hooks/useTrackOnboardingPageOnMount/useTrackOnboardingPageOnMount'
import { withPageErrorRecovery } from 'lib/hocs/withPageErrorRecovery/withPageErrorRecovery'
import { LogInLink } from '../components/LogInLink/LogInLink'
import { PayersGetStartedForm } from './components/PayersGetStartedForm/PayersGetStartedForm'
import * as events from './PayersGetStartedPage.events'
import { BusinessTypeEnum, type BusinessIndustryEnum, SignupInterestsEnum } from 'gql/types'

export interface PayersGetStartedPageFormValues {
  firstName: string
  lastName: string
  email: string
  businessName: string
  website: string
  businessIndustry: Maybe<BusinessIndustryEnum>
  prohibitedIndustryConfirmation: boolean
}

export interface PayersGetStartedPageProps {
  invitationCode: string | null
  email: string | null
  firstName: string | null
  lastName: string | null
  businessName: string | null
  website: string | null
  businessIndustry: Maybe<BusinessIndustryEnum>
  prohibitedIndustryConfirmation: boolean
  partner_source: string | null
  utm_source: string | null
  utm_campaign: string | null
  source_page: string | null
}

export const PayersGetStartedPage = withPageErrorRecovery<PayersGetStartedPageProps>(
  ({
    invitationCode,
    email,
    firstName,
    lastName,
    businessName,
    website,
    businessIndustry,
    prohibitedIndustryConfirmation,
    partner_source,
    utm_source,
    utm_campaign,
    source_page,
  }) => {
    useLogEventOnMount(events.OPEN, { partnerSource: partner_source })
    const { t } = useTranslation()
    const interestedIn = [SignupInterestsEnum.WORKING_CAPITAL]

    useTrackOnboardingPageOnMount({
      interestedIn,
      step: 'Get Started',
    })

    const form = useForm<PayersGetStartedPageFormValues>({
      defaultValues: {
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        email: email ?? '',
        businessName: businessName ?? '',
        website: website ?? '',
        businessIndustry: businessIndustry ?? null,
        prohibitedIndustryConfirmation: prohibitedIndustryConfirmation ?? false,
      },
    })

    return (
      <OnboardingLayoutProvider interestedIn={interestedIn} isSimplifiedFlow={true}>
        <OnboardingLayout businessType={BusinessTypeEnum.PAYER} withProgressBar={true}>
          <Head>
            <MetaTags
              title={t('onboarding::getStarted::meta::title')}
              description={t('onboarding::getStarted::meta::description')}
            />
          </Head>
          <Stack
            data-testid="payers-get-started-page"
            sx={{
              gap: 3.5,
              alignItems: 'center',
            }}
          >
            <OnboardingHeader maxWidth={490} title={t('onboarding::getStarted::title')} />
            <FormProvider {...form}>
              <PayersGetStartedForm
                invitationCode={invitationCode}
                partner_source={partner_source}
                utm_source={utm_source}
                utm_campaign={utm_campaign}
                source_page={source_page}
              />
            </FormProvider>
            <LogInLink eventContext={{ step: 'Get Started' }} />
          </Stack>
        </OnboardingLayout>
      </OnboardingLayoutProvider>
    )
  },
)
